import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Copy, Check, ExternalLink, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { CheckoutSession } from "@/hooks/useSales";

interface CheckoutLinkDisplayProps {
  checkoutSession: CheckoutSession;
  onRenew?: () => void;
  isRenewing?: boolean;
}

export function CheckoutLinkDisplay({ 
  checkoutSession, 
  onRenew,
  isRenewing 
}: CheckoutLinkDisplayProps) {
  const [copied, setCopied] = useState(false);

  const checkoutUrl = `${window.location.origin}/checkout/${checkoutSession.id}`;
  const expiresDate = new Date(checkoutSession.expires_at);
  const noExpiration = expiresDate.getFullYear() >= 9999;
  const isExpired = !noExpiration && expiresDate < new Date();
  const isPaid = checkoutSession.status === 'completed';

  const handleCopy = async () => {
    await navigator.clipboard.writeText(checkoutUrl);
    setCopied(true);
    toast.success("Link copiado!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleOpen = () => {
    window.open(checkoutUrl, "_blank");
  };

  const getStatusBadge = () => {
    if (isPaid) {
      return <Badge variant="default">Pago</Badge>;
    }
    if (isExpired) {
      return <Badge variant="destructive">Expirado</Badge>;
    }
    return <Badge variant="secondary">Ativo</Badge>;
  };

  return (
    <div className="space-y-3 p-4 rounded-lg border bg-muted/30">
      <div className="flex items-center justify-between">
        <h4 className="font-medium text-sm">Link de Checkout</h4>
        {getStatusBadge()}
      </div>
      
      <div className="flex gap-2">
        <Input 
          value={checkoutUrl} 
          readOnly 
          className="flex-1 text-sm"
        />
        <Button
          variant="outline"
          size="icon"
          onClick={handleCopy}
          disabled={isExpired && !isPaid}
        >
          {copied ? (
            <Check className="h-4 w-4 text-primary" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </Button>
        <Button
          variant="outline"
          size="icon"
          onClick={handleOpen}
          disabled={isExpired && !isPaid}
        >
          <ExternalLink className="h-4 w-4" />
        </Button>
      </div>

      {isExpired && !isPaid && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-destructive">
            Link expirado - edite a venda para renovar
          </p>
          {onRenew && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onRenew}
              disabled={isRenewing}
            >
              <RefreshCw className={`h-3 w-3 mr-1 ${isRenewing ? 'animate-spin' : ''}`} />
              Renovar
            </Button>
          )}
        </div>
      )}

      {!isExpired && !isPaid && (
        <p className="text-xs text-muted-foreground">
          {noExpiration
            ? "Sem expiracao"
            : `Expira em ${Math.max(0, Math.round((expiresDate.getTime() - Date.now()) / 60000))} minutos`}
        </p>
      )}
    </div>
  );
}
